import {
  Expand06Icon,
  Lock01Icon,
  RefreshCcw01Icon,
  ZoomInIcon,
  ZoomOutIcon,
} from '@langchain/untitled-ui-icons';

import { useContext, useState } from 'react';
import { MiniMap, useReactFlow } from 'reactflow';

import { ButtonGroup, IconButton, Text } from '@/design-system';
import { cn } from '@/design-system/utils/cn';
import { useColorScheme } from '@/hooks/useColorScheme';

import { StudioFeatureFlagsContext } from '../../../hooks/useStudioFeatureFlagsContext';
import {
  hashString,
  hslaToStr,
  hslaWithMinLightness,
  numberToColor,
} from '../../../utils';
import { MAX_ZOOM, MINIMAP_HEIGHT, MINIMAP_WIDTH, MIN_ZOOM } from './constants';
import { overrideReservedLabelsColor } from './graph.utils';
import { useGraphStore } from './store';

export const GraphZoomControls = ({
  zoomLevel,
  resetLayout,
}: {
  zoomLevel: number;
  resetLayout: () => void;
}) => {
  const { showGraphZoomControls } = useContext(StudioFeatureFlagsContext);

  const { isDarkMode } = useColorScheme();
  const { zoomIn, zoomOut } = useReactFlow();
  const [miniMapExpanded, setMiniMapExpanded] = useState(false);
  const fitToViewMode = useGraphStore((state) => state.fitToViewMode);

  if (!showGraphZoomControls) return null;

  return (
    <div className="absolute -top-[54px] left-4 flex h-fit items-start gap-4">
      <div className="z-[100] flex w-fit flex-col gap-2">
        <IconButton
          icon={ZoomInIcon}
          onClick={() => zoomIn()}
          label="Zoom in"
          color="secondary"
          variant="plain"
          className="w-fit"
          tooltipProps={{
            side: 'right',
            title: fitToViewMode ? 'Fit to view mode is enabled' : undefined,
          }}
          disabled={zoomLevel === MAX_ZOOM || fitToViewMode}
        />
        <IconButton
          icon={ZoomOutIcon}
          onClick={() => zoomOut()}
          label="Zoom in"
          color="secondary"
          variant="plain"
          className="w-fit"
          tooltipProps={{
            side: 'right',
            title: fitToViewMode ? 'Fit to view mode is enabled' : undefined,
          }}
          disabled={zoomLevel === MIN_ZOOM || fitToViewMode}
        />
        <FitToViewButton />
        <IconButton
          icon={RefreshCcw01Icon}
          onClick={resetLayout}
          label="Reset layout to default"
          color="secondary"
          variant="plain"
          className="w-fit"
          tooltipProps={{
            side: 'right',
          }}
        />
      </div>
      <div
        onMouseEnter={() => setMiniMapExpanded(true)}
        onMouseLeave={() => setMiniMapExpanded(false)}
        className={cn(
          'absolute left-10 transition-all duration-200 ease-in-out',
          !fitToViewMode && 'cursor-grab'
        )}
        style={{
          transform: miniMapExpanded
            ? 'translateX(50%) translateY(50%) scale(2)'
            : 'translateX(0%) translateY(0%) scale(1)',
        }}
      >
        <MiniMap
          ariaLabel="Mini map"
          nodeColor={(node) => {
            const hsla = overrideReservedLabelsColor(
              { label: node.data.label, isDarkMode },
              numberToColor(hashString(node.data.label))
            );

            return hslaToStr(hslaWithMinLightness(hsla, isDarkMode ? 40 : 60));
          }}
          nodeBorderRadius={12}
          maskColor={
            isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'
          }
          style={{
            backgroundColor: isDarkMode
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.1)',
            position: 'relative',
            margin: 0,
            width: MINIMAP_WIDTH,
            height: MINIMAP_HEIGHT,
          }}
          pannable={!fitToViewMode}
          zoomable={!fitToViewMode}
        />
      </div>
    </div>
  );
};

const FitToViewButton = () => {
  const fitToViewMode = useGraphStore((state) => state.fitToViewMode);
  const setFitToViewMode = useGraphStore((state) => state.setFitToViewMode);
  const { fitView } = useReactFlow();
  const [isHovering, setIsHovering] = useState(false);

  return (
    <ButtonGroup
      className="group transition-all duration-200"
      color={fitToViewMode ? 'primary' : 'secondary'}
      variant={fitToViewMode ? 'normal' : 'plain'}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <IconButton
        icon={Expand06Icon}
        onClick={() => {
          if (fitToViewMode) {
            setFitToViewMode(false);
            setIsHovering(false);
            return;
          }
          fitView({ padding: 0.2 });
        }}
        label={fitToViewMode ? 'Disable fit to view' : 'Fit graph to view'}
        color={fitToViewMode ? 'primary' : 'secondary'}
        variant={'plain'}
      />
      {isHovering && !fitToViewMode && (
        <IconButton
          icon={Lock01Icon}
          onClick={() => {
            setFitToViewMode(!fitToViewMode);
            fitView({ padding: 0.2 });
          }}
          label="Lock fit to view"
          color={fitToViewMode ? 'primary' : 'secondary'}
          variant={'plain'}
          tooltipProps={{
            side: 'right',
            title: (
              <div className="flex max-w-[200px] flex-col gap-2">
                <Text variant="sm">Lock fit to view</Text>
                <Text variant="xs" color="secondary">
                  As you interact, the graph will re-size to fit the space.
                </Text>
              </div>
            ),
          }}
          className="hidden duration-300 animate-in slide-in-from-left-4 group-hover:block"
        />
      )}
    </ButtonGroup>
  );
};
